<template>
  <div :class="bem()">
    <VisionCard aspect-ratio="1/1" :bg-gradient="bgGradient" :bg-colour="bgColour" :scrim="0" :full-height-content="fullHeightContent" :alignment="alignment" :justification="justification">
        <template #content>
          <div :class="bem('content-wrapper')">
            <template v-if="props.item.cardType === 'paragraph-text'">
              <div :class="bem('paragraph-text-content')">
                {{ props.item.paragraphTextContent }}
              </div>
              <div v-if="props.item.paragraphTextFooterTitle || props.item.paragraphTextFooterSubtitle" :class="bem('paragraph-text-footer')">
                <span v-if="props.item.paragraphTextFooterTitle" :class="bem('paragraph-text-footer-title')">{{ props.item.paragraphTextFooterTitle }}</span>
                <span v-if="props.item.paragraphTextFooterSubtitle" :class="bem('paragraph-text-footer-subtitle')">{{ props.item.paragraphTextFooterSubtitle }}</span>
              </div>
            </template>
            <template v-if="props.item.cardType === 'image'">
              <DatoCmsImage
                v-if="props.item.image"
                :data="props.item.image?.responsiveImage"
                :class="bem('image')"
                sizes="(max-width: 1280px) 100vw, 40vw"
              />
            </template>
          </div>
        </template>
        <template v-if="props.item.cardBackgroundImage" #image>
          <DatoCmsImage
            :data="props.item.cardBackgroundImage.responsiveImage"
            object-fit="cover"
            :object-position="`${props.item.cardBackgroundImage.focalPoint.x * 100}% ${
              props.item.cardBackgroundImage.focalPoint.y * 100
            }%`"
            layout="fill"
            :class="bem('image')"
            sizes="(max-width: 1280px) 100vw, 40vw"
          />
        </template>
    </VisionCard>
  </div>
</template>

<script setup lang="ts">
import { Image as DatoCmsImage } from 'vue-datocms'
import tailwindConfig from '@/tailwind.config.js'
import resolveConfig from 'tailwindcss/resolveConfig'

const resolvedConfig = resolveConfig(tailwindConfig)

interface CardScrollerCardProps {
  item: {
    id: string
    cardType: 'paragraph-text' | 'image' | 'branded-heading'
    cardBackgroundColour: string
    cardBackgroundImage?: object
    image?: object
    paragraphTextContent: object
    paragraphTextFooterTitle: string
    paragraphTextFooterSubtitle: string
    brandedHeading: {
      heading: object
      size: string
    }
  }
}

const props = defineProps<CardScrollerCardProps>()

const bem = useBEM('CardScrollerCard')

const bgGradient = computed(() => {
  if (props.item.cardBackgroundImage) return ''

  if (props.item.cardBackgroundColour === 'default-gradient') return 'default'

  return ''
})

const fullHeightContent = computed(() => {
  return props.item.cardType !== 'image'
})

const alignment = computed(() => {
  if (props.item.cardType === 'image') {
    return 'center'
  }

  return 'flex-end'
})

const justification = computed(() => {
  if (props.item.cardType === 'image') {
    return 'center'
  }

  return 'left'
})

const bgColour = computed(() => {
  if (props.item.cardBackgroundColour === 'white') {
    return resolvedConfig.theme.colors.white
  }

  return resolvedConfig.theme.colors.gray['300']
})
</script>

<style lang="postcss" scoped>
.CardScrollerCard {
  container: card-scroller-card / inline-size;
}

.CardScrollerCard__content-wrapper {
  @apply h-full flex flex-col justify-between;
}

/** Paragraph Text Variant */
.CardScrollerCard__paragraph-text-content {
  @apply text-xxs font-semibold;

  @container card-scroller-card (min-width: 350px) {
    @apply text-xs;
  }

  @container card-scroller-card (min-width: 400px) {
    @apply text-base;
  }
}

.CardScrollerCard__paragraph-text-footer {
  @apply flex flex-col gap-1 text-[10px];
}

.CardScrollerCard__paragraph-text-footer-title {
  @apply font-semibold text-xxs;
}

.CardScrollerCard__paragraph-text-footer-subtitle {
  @apply font-fragment-mono text-tiny;
}
</style>